import { useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Input, Tooltip, message as antdMessage } from "antd";
import {
  CloseOutlined,
  FileOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useChat } from "src/shared/hooks/useChat";
import {
  selectIsSendingMessage,
  selectIsOnline,
} from "src/domain/app/redux/chat/chat-selectors";
import { uploadFileService } from "src/shared/services/upload-file";
import { FolderName } from "src/shared/constants/AppEnums";
import {
  CHAT_ATTACHMENT_EXTENSIONS,
  CHAT_MAX_ATTACHMENTS,
  CHAT_MAX_ATTACHMENT_SIZE_MB,
  formatFileSize,
  isAllowedChatAttachment,
} from "src/shared/services/chat/attachments";
import "./styles.less";

const { TextArea } = Input;

interface MessageInputProps {
  roomId: string;
}

const MessageInput: React.FC<MessageInputProps> = ({ roomId }) => {
  const { messages, formatMessage } = useIntl();
  const { sendMessage } = useChat();
  const isSendingMessage = useSelector(selectIsSendingMessage);
  const isOnline = useSelector(selectIsOnline);
  const [messageText, setMessageText] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isBusy = isSendingMessage || isUploading;
  const canSend =
    !isBusy && (!!messageText.trim() || pendingFiles.length > 0);

  const addFiles = (files: File[]) => {
    const accepted: File[] = [];

    for (const file of files) {
      if (!isAllowedChatAttachment(file.name)) {
        antdMessage.error(
          `${messages["common.supportedTypes"]}: ${CHAT_ATTACHMENT_EXTENSIONS.join(" / ")}`,
        );
        continue;
      }
      if (file.size > CHAT_MAX_ATTACHMENT_SIZE_MB * 1024 * 1024) {
        antdMessage.error(
          formatMessage(
            { id: "chat.fileTooLarge" },
            { name: file.name, max: CHAT_MAX_ATTACHMENT_SIZE_MB },
          ),
        );
        continue;
      }
      accepted.push(file);
    }

    if (pendingFiles.length + accepted.length > CHAT_MAX_ATTACHMENTS) {
      antdMessage.error(
        formatMessage(
          { id: "chat.maxAttachments" },
          { max: CHAT_MAX_ATTACHMENTS },
        ),
      );
    }

    setPendingFiles((current) =>
      [...current, ...accepted].slice(0, CHAT_MAX_ATTACHMENTS),
    );
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    // Reset so selecting the same file again still triggers onChange
    e.target.value = "";
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData.files ?? []);
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((current) => current.filter((_, i) => i !== index));
  };

  const uploadPendingFiles = async () => {
    const formData = new FormData();
    pendingFiles.forEach((file) => formData.append("Files", file));
    formData.append("FolderName", `${FolderName.ChatAttachments}`);

    const response = await uploadFileService.uploadFilesList(formData);
    if (!response?.Data?.length) {
      throw new Error("Upload returned no files");
    }
    return response.Data.map((file) => file.id);
  };

  const handleSendMessage = async () => {
    if (!canSend) {
      return;
    }

    if (pendingFiles.length > 0 && !isOnline) {
      antdMessage.warning(messages["chat.attachmentsOffline"] as string);
      return;
    }

    let attachmentFileIds: string[] | undefined;
    if (pendingFiles.length > 0) {
      try {
        setIsUploading(true);
        attachmentFileIds = await uploadPendingFiles();
      } catch (error) {
        console.error("Failed to upload attachments:", error);
        antdMessage.error(messages["chat.fileUploadFailed"] as string);
        return;
      } finally {
        setIsUploading(false);
      }
    }

    try {
      const sent = await sendMessage({
        chatRoomId: roomId,
        message: messageText.trim(),
        attachmentFileIds,
      });

      // Keep the draft when sending failed online so the user can retry
      if (!sent && isOnline) {
        return;
      }

      setMessageText("");
      setPendingFiles([]);
      inputRef.current?.focus();
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="message-input-container">
      {!isOnline && (
        <div className="offline-indicator">
          {messages["chat.offline"] as string}
        </div>
      )}

      {pendingFiles.length > 0 && (
        <div className="pending-attachments">
          {pendingFiles.map((file, index) => (
            <div
              key={`${file.name}-${file.size}-${index}`}
              className="pending-attachment"
              title={file.name}
            >
              <FileOutlined className="pending-attachment-icon" />
              <span className="pending-attachment-name">{file.name}</span>
              <span className="pending-attachment-size">
                {formatFileSize(file.size)}
              </span>
              {!isBusy && (
                <CloseOutlined
                  className="pending-attachment-remove"
                  onClick={() => removePendingFile(index)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="input-wrapper">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={CHAT_ATTACHMENT_EXTENSIONS.join(",")}
          onChange={handleFileInputChange}
          style={{ display: "none" }}
        />
        <Tooltip title={messages["chat.attach"] as string}>
          <PaperClipOutlined
            className={`attach-btn ${isBusy || pendingFiles.length >= CHAT_MAX_ATTACHMENTS ? "disabled" : ""}`}
            onClick={() => {
              if (!isBusy && pendingFiles.length < CHAT_MAX_ATTACHMENTS) {
                fileInputRef.current?.click();
              }
            }}
          />
        </Tooltip>

        <TextArea
          ref={inputRef}
          placeholder={messages["chat.typeMessage"] as string}
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyPress={handleKeyPress}
          onPaste={handlePaste}
          autoSize={{ minRows: 1, maxRows: 4 }}
          disabled={isBusy}
          className="message-input"
        />

        {isUploading ? (
          <LoadingOutlined className="send-btn active" />
        ) : (
          <SendOutlined
            className={`send-btn ${canSend ? "active" : ""}`}
            onClick={handleSendMessage}
          />
        )}
      </div>
    </div>
  );
};

export default MessageInput;
